/**
 * Administração › Feriados: calendário que o perfil do dia da semana deixa de fora — Nexora Grid (frontend, US-3002).
 *
 * Criado em: 25/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * Decisões do responsável do produto (25/10/2026):
 * - **nacionais** pela planilha oficial da ANBIMA (`feriados_nacionais.xls`): a equipe Nexora baixa o arquivo e envia
 *   aqui (`importar_feriados_nacionais`); o sistema não acessa a internet;
 * - **estaduais, municipais e eventos** cadastrados à mão pela engenharia ou pelo administrador (`editar_feriados`),
 *   com a fonte (nº da lei ou do ato).
 * Apagar é marcar: pede o motivo e a linha continua no histórico ("mostrar apagados").
 * v2.0 (US-4604, Sprint 46): migalhas, KPIs por tipo que filtram, busca com `/` e ano, tipo e apagados no endereço,
 * lista densa com painel de detalhe ao lado, motivo de apagar num diálogo próprio (sai o `window.prompt`), importação e
 * cadastro em blocos com validação ao sair do campo. Mesmas chamadas e permissões de antes.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { quando } from "./tipos";
import {
  avisar, BarraFiltros, CabecalhoTela, Campo, DialogoMotivo, EstadoVazio, Esqueleto, FaixaKpis, ListaDetalhe, Selo, TabelaDensa,
  useLembrarEndereco, useParametro, type Coluna,
} from "../componentes/Tela";
import { casaBusca, ordenar, type Ordem } from "../componentes/regrasTela";
import "./estrutura.css";

type Feriado = { id: number; data: string; nome: string; tipo: "nacional" | "estadual" | "municipal" | "evento"; uf: string; municipio: string;
  origem: "oficial" | "manual"; fonte: string; criado_por: string; apagado_em: string | null; apagado_por: string | null; motivo_apagado: string | null };
type Importacao = { arquivo: string; lidos: number; incluidos: number; primeiro_ano: number; ultimo_ano: number; importado_por: string; importado_em: string };
const TIPOS: Record<Feriado["tipo"], string> = { nacional: "Nacional", estadual: "Estadual", municipal: "Municipal", evento: "Evento (dia atípico)" };
const URL_ANBIMA = "https://www.anbima.com.br/feriados/arqs/feriados_nacionais.xls";
const dataBr = (iso: string) => iso.split("-").reverse().join("/");
const DIAS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const diaSemana = (iso: string) => DIAS[new Date(`${iso}T12:00:00`).getDay()] ?? "";

export function Feriados() {
  const { usuario, empresa } = useSessao();
  useLembrarEndereco();
  const anoAtual = String(new Date().getFullYear());
  const [anoTexto, setAnoTexto] = useParametro("ano", anoAtual);
  const ano = Number(anoTexto) || Number(anoAtual);
  const [apagadosTexto, setApagadosTexto] = useParametro("apagados");
  const apagados = apagadosTexto === "1";
  const [tipo, setTipo] = useParametro("tipo");
  const [busca, setBusca] = useParametro("busca");
  const [sel, setSel] = useParametro("sel");
  const [ordem, setOrdem] = useState<Ordem>({ coluna: "data", crescente: true });
  const [painelAberto, setPainelAberto] = useState(false);
  const [feriados, setFeriados] = useState<Feriado[] | null>(null);
  const [importacao, setImportacao] = useState<Importacao | null>(null);
  const [municipios, setMunicipios] = useState<{ codigo: string; nome: string; uf: string }[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [apagando, setApagando] = useState<Feriado | null>(null);
  const [novo, setNovo] = useState({ tipo: "municipal" as "estadual" | "municipal" | "evento", data: "", nome: "", municipio: "", uf: "", fonte: "" });
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const carregar = useCallback(() => {
    if (!empresa) return;
    api<{ feriados: Feriado[]; ultima_importacao_nacional: Importacao | null }>(`/v1/feriados?empresa=${empresa}&ano=${ano}${apagados ? "&incluir_apagados=true" : ""}`)
      .then((r) => { setFeriados(r.feriados); setImportacao(r.ultima_importacao_nacional); }).catch(() => setFeriados([]));
  }, [empresa, ano, apagados]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    if (!empresa) return;
    api<{ municipios: { codigo: string; nome: string; uf: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`)
      .then((r) => setMunicipios(r.municipios.filter((m) => m.cadastrado))).catch(() => setMunicipios([]));
  }, [empresa]);
  const nomeMunicipio = useCallback((codigo: string) => municipios.find((m) => m.codigo === codigo)?.nome ?? codigo, [municipios]);
  const ondeVale = useCallback((f: Feriado) => (f.tipo === "nacional" ? "Brasil" : f.tipo === "estadual" ? f.uf : nomeMunicipio(f.municipio)), [nomeMunicipio]);
  const contagem = useMemo(() => {
    const c = { total: 0, nacional: 0, estadual: 0, municipal: 0, evento: 0, apagados: 0 };
    for (const f of feriados ?? []) { if (f.apagado_em) { c.apagados++; continue; } c.total++; c[f.tipo]++; }
    return c;
  }, [feriados]);
  const visiveis = useMemo(() => {
    const filtrados = (feriados ?? []).filter((f) => (!tipo || f.tipo === tipo) && casaBusca(busca, f.nome, f.fonte, ondeVale(f), TIPOS[f.tipo], dataBr(f.data)));
    const valor = (f: Feriado) => ordem.coluna === "nome" ? f.nome : ordem.coluna === "tipo" ? TIPOS[f.tipo] : ordem.coluna === "onde" ? ondeVale(f) : f.data;
    return ordenar(filtrados, valor, ordem.crescente);
  }, [feriados, tipo, busca, ordem, ondeVale]);
  if (!usuario || !empresa) return null;
  const edita = pode.editarFeriados(usuario);
  const importa = pode.importarFeriadosNacionais(usuario);
  const podeApagar = (f: Feriado) => !f.apagado_em && (f.tipo === "nacional" ? importa : edita);
  const selecionado = visiveis.find((f) => String(f.id) === sel) ?? visiveis[0] ?? null;
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); if (ok) { setAviso(null); avisar(ok); } carregar(); return true; }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); return false; }
  };
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!arquivo) return;
    const form = new FormData();
    form.append("arquivo", arquivo);
    void executar(async () => {
      const r = await api<{ lidos: number; incluidos: number; primeiro_ano: number; ultimo_ano: number }>(`/v1/feriados/nacionais/importar?empresa=${empresa}`, { form });
      setAviso(`Planilha lida: ${r.lidos} feriados de ${r.primeiro_ano} a ${r.ultimo_ano}; ${r.incluidos} novos incluídos.`);
    }, "").then((ok) => { if (ok) setArquivo(null); });
  };
  const erroNovo = (k: string): string | null => {
    if (!tocados.has(k)) return null;
    if (k === "data") return novo.data ? null : "Escolha a data.";
    if (k === "nome") return novo.nome.trim().length >= 2 ? null : "Escreva o nome (2 letras ou mais).";
    if (k === "uf") return novo.uf ? null : "Escolha a UF.";
    if (k === "municipio") return novo.municipio ? null : "Escolha o município.";
    return null;
  };
  const tocar = (k: string) => setTocados((t) => new Set(t).add(k));
  const cadastrar = (e: FormEvent) => {
    e.preventDefault();
    const corpo = { empresa, tipo: novo.tipo, data: novo.data, nome: novo.nome, fonte: novo.fonte,
      ...(novo.tipo === "estadual" ? { uf: novo.uf } : { municipio: novo.municipio }) };
    void executar(() => api("/v1/feriados", { corpo }), "Feriado cadastrado: o perfil do dia da semana passa a deixá-lo de fora.")
      .then((ok) => { if (ok) { setNovo({ ...novo, data: "", nome: "", fonte: "" }); setTocados(new Set()); } });
  };
  const apagar = async (f: Feriado, motivo: string) => {
    await executar(() => api(`/v1/feriados/${f.id}?empresa=${empresa}&motivo=${encodeURIComponent(motivo.trim())}`, { metodo: "DELETE" }), "Feriado apagado (fica no histórico).");
    setApagando(null);
  };
  const ufs = [...new Set(municipios.map((m) => m.uf).filter(Boolean))];
  const pilulas = [
    ...(tipo ? [{ chave: "tipo", texto: TIPOS[tipo as Feriado["tipo"]] ?? tipo }] : []),
    ...(apagados ? [{ chave: "apagados", texto: "Com os apagados" }] : []),
    ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (chave: string) => ({ tipo: setTipo, apagados: setApagadosTexto, busca: setBusca } as Record<string, (v: string) => void>)[chave]?.("");
  const limpar = () => { setTipo(""); setApagadosTexto(""); setBusca(""); };
  const colunas: Coluna<Feriado>[] = [
    { chave: "data", titulo: "Data", ordenavel: true, celula: (f) => <span className="tl-mono">{dataBr(f.data)} <small className="nx-suave">{diaSemana(f.data)}</small></span> },
    { chave: "nome", titulo: "Feriado", ordenavel: true, quebra: true, celula: (f) => <>{f.nome}{f.apagado_em && <small className="nx-suave"> · apagado por {f.apagado_por} ({f.motivo_apagado})</small>}</> },
    { chave: "tipo", titulo: "Tipo", ordenavel: true, celula: (f) => TIPOS[f.tipo] },
    { chave: "onde", titulo: "Onde vale", ordenavel: true, celula: (f) => ondeVale(f) },
    { chave: "fonte", titulo: "Fonte", quebra: true, celula: (f) => <small className="nx-suave">{f.origem === "oficial" ? "oficial" : "manual"}{f.fonte ? ` · ${f.fonte}` : ""}</small> },
  ];
  return (
    <section className="tl-pagina admin-estrutura">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[{ texto: NOMES.administracao }, { texto: NOMES.feriados }]} titulo={NOMES.feriados}
        subtitulo="dias que saem do padrão de cada dia da semana" />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      <p className="nx-suave" style={{ margin: 0 }}>Os dias abaixo saem do padrão de cada dia da semana que o agente usa para comparar a operação (um feriado na segunda-feira não vira "segunda normal").</p>
      {!feriados ? <Esqueleto linhas={6} /> : (
        <FaixaKpis ativo={tipo} aoEscolher={setTipo} itens={[
          { chave: "", rotulo: `Feriados em ${ano}`, valor: contagem.total, detalhe: contagem.apagados ? `${contagem.apagados} apagados` : "valendo" },
          { chave: "nacional", rotulo: "Nacionais", valor: contagem.nacional, detalhe: "planilha da ANBIMA" },
          { chave: "estadual", rotulo: "Estaduais", valor: contagem.estadual, detalhe: "por UF" },
          { chave: "municipal", rotulo: "Municipais", valor: contagem.municipal, detalhe: "por município" },
          { chave: "evento", rotulo: "Eventos", valor: contagem.evento, detalhe: "dias atípicos" },
        ]} />
      )}
      <BarraFiltros busca={busca} aoBuscar={setBusca} placeholder="Buscar por nome, fonte, município ou data" pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
        <button type="button" className="nx-botao" onClick={() => setAnoTexto(String(ano - 1))} aria-label="Ano anterior">‹ {ano - 1}</button>
        <span className="ano-atual" aria-live="polite">{ano}</span>
        <button type="button" className="nx-botao" onClick={() => setAnoTexto(String(ano + 1))} aria-label="Próximo ano">{ano + 1} ›</button>
        <label className="opcao"><input type="checkbox" checked={apagados} onChange={(e) => setApagadosTexto(e.target.checked ? "1" : "")} /> mostrar apagados</label>
      </BarraFiltros>
      {feriados && (
        <ListaDetalhe aberto={painelAberto}
          lista={(
            <TabelaDensa rotulo="Feriados" colunas={colunas} linhas={visiveis} chave={(f) => String(f.id)} selecionada={selecionado ? String(selecionado.id) : null}
              aoSelecionar={(f) => { setSel(String(f.id)); setPainelAberto(true); }} ordem={ordem} aoOrdenar={setOrdem}
              cabecalho={<span><b>{visiveis.length}</b> de {feriados.length} dias em {ano}</span>}
              vazio={<EstadoVazio titulo={pilulas.length ? "Nenhum feriado com esses filtros" : `Nenhum feriado em ${ano}`}
                texto={pilulas.length ? "Tire um filtro." : importa ? "Importe a planilha da ANBIMA ou cadastre o feriado abaixo." : edita ? "Cadastre o feriado abaixo." : undefined}
                acao={pilulas.length ? <button type="button" className="nx-botao" onClick={limpar}>Limpar filtros</button> : undefined} />}
              cartao={(f) => (
                <>
                  <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}>
                    <b className="tl-mono">{dataBr(f.data)}</b>{f.apagado_em ? <Selo tom="suave">apagado</Selo> : <Selo>{TIPOS[f.tipo]}</Selo>}</div>
                  <span style={{ fontSize: 13 }}>{f.nome}</span>
                  <span className="nx-suave">{ondeVale(f)}</span>
                </>
              )} />
          )}
          detalhe={selecionado ? (
            <div className="tl-det-corpo">
              <div className="tl-det-titulo"><div style={{ flex: 1, minWidth: 0 }}><h2>{selecionado.nome}</h2>
                <p>{dataBr(selecionado.data)} · {diaSemana(selecionado.data)}</p></div>
                <button type="button" className="tl-fantasma tl-fechar" onClick={() => setPainelAberto(false)} aria-label="Fechar o detalhe">×</button></div>
              <div className="tl-linha-acoes"><Selo tom={selecionado.apagado_em ? "suave" : undefined}>{selecionado.apagado_em ? "Apagado" : TIPOS[selecionado.tipo]}</Selo></div>
              <dl className="tl-kv">
                <dt>Tipo</dt><dd>{TIPOS[selecionado.tipo]}</dd>
                <dt>Onde vale</dt><dd>{ondeVale(selecionado)}</dd>
                <dt>Origem</dt><dd>{selecionado.origem === "oficial" ? "oficial (ANBIMA)" : "manual"}</dd>
                <dt>Fonte</dt><dd>{selecionado.fonte || "—"}</dd>
                <dt>Cadastrado por</dt><dd>{selecionado.criado_por || "—"}</dd>
                {selecionado.apagado_em && <><dt>Apagado</dt><dd>{quando(selecionado.apagado_em)} por {selecionado.apagado_por}: {selecionado.motivo_apagado}</dd></>}
              </dl>
              {podeApagar(selecionado) && (
                <div className="tl-linha-acoes"><button type="button" className="nx-botao" onClick={() => setApagando(selecionado)}>Apagar</button></div>
              )}
            </div>
          ) : undefined} />
      )}

      <div className="estrutura-duas">
        <section className="tl-bloco admin-secao" aria-label="Feriados nacionais">
          <header><h3>Feriados nacionais (ANBIMA)</h3></header>
          <div className="admin-corpo">
            <p className="nx-suave" style={{ margin: 0 }}>
              {importacao ? <>Última importação: <b>{importacao.arquivo}</b> ({importacao.primeiro_ano}–{importacao.ultimo_ano}, {importacao.lidos} feriados), por {importacao.importado_por} em {quando(importacao.importado_em)}.</>
                : "Nenhuma planilha nacional importada ainda."}
            </p>
            {importa && (
              <form className="form-linha" onSubmit={enviar} aria-label="Importar feriados nacionais">
                <p className="nx-suave tl-campo largo" style={{ margin: 0, gridColumn: "1 / -1" }}>Baixe a planilha oficial em <a href={URL_ANBIMA} target="_blank" rel="noreferrer">anbima.com.br — feriados nacionais (.xls)</a> e envie aqui. Só as datas novas são incluídas; nada é apagado.</p>
                <Campo id="fe-arquivo" rotulo="Planilha da ANBIMA (.xls) ou CSV" largo>
                  <input id="fe-arquivo" type="file" accept=".xls,.csv" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
                </Campo>
                <div className="tl-linha-acoes"><button type="submit" className="nx-botao" disabled={!arquivo}>Importar feriados nacionais</button></div>
              </form>
            )}
          </div>
        </section>

        {edita && (
          <section className="tl-bloco admin-secao" aria-label="Cadastrar feriado">
            <header><h3>Novo feriado</h3><span className="tl-subtitulo">estadual, municipal ou evento</span></header>
            <div className="admin-corpo">
              <form className="form-linha" onSubmit={cadastrar} aria-label="Novo feriado">
                <Campo id="fe-tipo" rotulo="Tipo">
                  <select id="fe-tipo" value={novo.tipo} onChange={(e) => setNovo({ ...novo, tipo: e.target.value as typeof novo.tipo })}>
                    <option value="municipal">Municipal</option><option value="estadual">Estadual</option><option value="evento">Evento (dia atípico)</option></select>
                </Campo>
                <Campo id="fe-data" rotulo="Data" erro={erroNovo("data")}>
                  <input id="fe-data" type="date" required value={novo.data} onBlur={() => tocar("data")} onChange={(e) => setNovo({ ...novo, data: e.target.value })} />
                </Campo>
                <Campo id="fe-nome" rotulo="Nome" erro={erroNovo("nome")} largo>
                  <input id="fe-nome" required minLength={2} maxLength={120} value={novo.nome} placeholder="ex.: aniversário da cidade" onBlur={() => tocar("nome")}
                    onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
                </Campo>
                {novo.tipo === "estadual" ? (
                  <Campo id="fe-uf" rotulo="UF" erro={erroNovo("uf")}>
                    <select id="fe-uf" required value={novo.uf} onBlur={() => tocar("uf")} onChange={(e) => setNovo({ ...novo, uf: e.target.value })}>
                      <option value="">—</option>{ufs.map((u) => <option key={u} value={u}>{u}</option>)}</select>
                  </Campo>
                ) : (
                  <Campo id="fe-municipio" rotulo="Município" erro={erroNovo("municipio")}>
                    <select id="fe-municipio" required value={novo.municipio} onBlur={() => tocar("municipio")} onChange={(e) => setNovo({ ...novo, municipio: e.target.value })}>
                      <option value="">—</option>{municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}</select>
                  </Campo>
                )}
                <Campo id="fe-fonte" rotulo="Fonte" ajuda="nº da lei ou do ato">
                  <input id="fe-fonte" maxLength={200} value={novo.fonte} placeholder="ex.: Lei municipal nº 123/1980" onChange={(e) => setNovo({ ...novo, fonte: e.target.value })} />
                </Campo>
                <div className="tl-linha-acoes"><button type="submit" className="nx-botao nx-botao-principal">Cadastrar feriado</button></div>
              </form>
            </div>
          </section>
        )}
      </div>

      <DialogoMotivo aberto={!!apagando} minimo={3} titulo="Apagar o feriado"
        texto={apagando ? `"${apagando.nome}" (${dataBr(apagando.data)}) deixa de valer para o perfil do dia da semana. O registro fica no histórico ("mostrar apagados").` : undefined}
        rotuloConfirmar="Apagar feriado" exemplo="ex.: data errada, cadastrado em dobro"
        aoConfirmar={(m) => (apagando ? apagar(apagando, m) : undefined)} aoCancelar={() => setApagando(null)} />
    </section>
  );
}
